import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Flame, Wrench, Hammer, Sparkles, ArrowUpRight } from 'lucide-react';

const iconMap = {
  Flame: Flame,
  Wrench: Wrench,
  Hammer: Hammer,
  Sparkles: Sparkles,
};

const services = [
  {
    id: 'welding',
    name: 'Welding',
    description: 'Professional welding services for industrial and residential projects.',
    icon: 'Flame',
    image: 'https://images.unsplash.com/photo-1745448797900-35d08e85e9db',
    span: 'col-span-2 row-span-2',
  },
  {
    id: 'mechanics',
    name: 'Mechanics',
    description: 'Expert automotive and machinery repair.',
    icon: 'Wrench',
    image: 'https://images.unsplash.com/photo-1544692414-636611c6ae73',
    span: 'col-span-1 row-span-1',
  },
  {
    id: 'handyman',
    name: 'Handyman',
    description: 'Skilled repairs and home improvements.',
    icon: 'Hammer',
    image: 'https://images.unsplash.com/photo-1766524555120-9c2e886c72f5',
    span: 'col-span-1 row-span-1',
  },
  {
    id: 'cleaning',
    name: 'Cleaning',
    description: 'Deep cleaning solutions for homes and businesses.',
    icon: 'Sparkles',
    image: 'https://images.unsplash.com/photo-1596468257587-0ef5ee6709e3',
    span: 'col-span-2 row-span-1',
  },
];

export const ServicesGrid = () => {
  return (
    <section className="py-24 px-6 md:px-12 lg:px-24 noise-bg" data-testid="services-grid">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-[#00F0FF] font-medium tracking-wider uppercase text-sm">
            What We Do
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-6">
            Our <span className="gradient-text">Services</span>
          </h2>
          <p className="text-gray-400 max-w-2xl text-lg">
            From heavy-duty welding to spotless cleaning, we bring professional 
            expertise to every job.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`service-card card-hover min-h-[200px] md:min-h-[280px] ${
                  index === 0 ? 'md:col-span-2 md:row-span-2 md:min-h-[580px]' : ''
                } ${index === 3 ? 'col-span-2' : ''}`}
                data-testid={`service-card-${service.id}`}
              >
                <div className="service-card-image">
                  <img src={service.image} alt={service.name} loading="lazy" />
                </div>
                <div className="service-card-overlay" />
                <div className="service-card-content">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#00F0FF]" />
                    </div>
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-2">
                    {service.name}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {service.description}
                  </p>
                  <Link
                    to={`/services#${service.id}`}
                    className="inline-flex items-center gap-2 text-[#00F0FF] hover:gap-3 transition-all group"
                  >
                    <span className="text-sm font-medium">Learn More</span>
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;
